import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  ArrowLeft,
  Store,
  User,
  Mail,
  Phone,
  Globe,
  Calendar,
  TrendingUp,
  ShoppingCart,
  DollarSign,
  Eye,
  ExternalLink,
  Package,
  Users,
  MapPin,
  Clock
} from 'lucide-react';
import api from '@/utils/api';
import { AdminPageSkeleton } from '@/components/admin/AdminPageSkeleton';
import { toast } from 'sonner';
import { formatPrice } from '@/utils/currency';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const BUSINESS_TYPES = [
  { id: "restaurant", label: "Restaurant & Cafe", icon: "🍽️" },
  { id: "retail", label: "Retail Store", icon: "🛍️" },
  { id: "beauty", label: "Beauty & Wellness", icon: "💄" },
  { id: "fitness", label: "Fitness & Sports", icon: "💪" },
  { id: "education", label: "Education & Training", icon: "📚" },
  { id: "healthcare", label: "Healthcare", icon: "🏥" },
  { id: "automotive", label: "Automotive", icon: "🚗" },
  { id: "home", label: "Home & Garden", icon: "🏠" },
  { id: "technology", label: "Technology", icon: "💻" },
  { id: "other", label: "Other", icon: "🏢" },
];

interface StoreDetails {
  _id: string;
  storeName: string;
  businessName: string;
  url: string;
  logo: string;
  currency: string;
  businessType: string;
  whatsappNumber?: string;
  description?: string;
  location?: string;
  createdAt: string;
  updatedAt: string;
  owner: {
    _id: string;
    name: string;
    email: string;
    whatsappNumber?: string;
    createdAt: string;
  };
  analytics: {
    views: number;
    orders: number;
    revenue: number;
    totalRevenue: number;
    completedOrders: number;
    pendingOrders: number;
    cancelledOrders: number;
    averageOrderValue: number;
    totalProducts: number;
    totalCustomers: number;
    bookings?: number;
  };
}

export function StoreDetailsPage() {
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();
  const [store, setStore] = useState<StoreDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingStore, setDeletingStore] = useState(false);

  useEffect(() => {
    if (storeId) {
      fetchStoreDetails();
    }
  }, [storeId]);

  const fetchStoreDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/platform-analytics/stores/${storeId}`);
      setStore(response.data);
    } catch (error) {
      console.error('Error fetching store details:', error);
      toast.error('Failed to fetch store details');
    } finally {
      setLoading(false);
    }
  };

  const getBusinessTypeLabel = (type: string) => {
    const businessType = BUSINESS_TYPES.find(bt => bt.id === type);
    return businessType ? `${businessType.icon} ${businessType.label}` : type;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const openStore = () => {
    window.open(`/${store?.url}`, '_blank');
  };

  const deleteStore = async () => {
    if (!storeId) return;
    setDeletingStore(true);
    try {
      await api.delete(`/api/platform-analytics/stores/${storeId}`);
      toast.success('Store deleted successfully');
      navigate('/admin/stores');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Failed to delete store');
    } finally {
      setDeletingStore(false);
      setShowDeleteDialog(false);
    }
  };

  if (loading) {
    return <AdminPageSkeleton variant="detail" kpiCount={4} />;
  }

  if (!store) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Store not found</p>
        <Button onClick={() => navigate('/admin/stores')} className="mt-4">
          Back to Stores
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
        <div className="flex items-start gap-4">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate('/admin/stores')}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-start gap-4">
            {store.logo ? (
              <img
                src={store.logo}
                alt={store.storeName}
                className="h-16 w-16 md:h-20 md:w-20 rounded-lg object-cover"
              />
            ) : (
              <div className="h-16 w-16 md:h-20 md:w-20 rounded-lg bg-muted flex items-center justify-center">
                <Store className="h-8 w-8 md:h-10 md:w-10 text-muted-foreground" />
              </div>
            )}
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight">{store.storeName}</h1>
              <p className="text-sm md:text-base text-muted-foreground">{store.businessName}</p>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <Badge variant="outline">
                  {getBusinessTypeLabel(store.businessType)}
                </Badge>
                <Button
                  variant="link"
                  size="sm"
                  onClick={openStore}
                  className="h-auto p-0 text-blue-600"
                >
                  <Globe className="h-3 w-3 mr-1" />
                  sqale.shop/{store.url}
                  <ExternalLink className="h-3 w-3 ml-1" />
                </Button>
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
          <Button onClick={openStore} className="w-full md:w-auto">
            <ExternalLink className="h-4 w-4 mr-2" />
            Visit Store
          </Button>
          <Button
            variant="destructive"
            onClick={() => setShowDeleteDialog(true)}
            className="w-full md:w-auto"
          >
            Delete store
          </Button>
        </div>
      </div>

      {/* Analytics Cards */}
      <Card className="p-4">
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-4 w-full">
          <Card className="p-4 shadow-none border-none bg-green-100/30 hover:bg-green-100/80 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-black">Total Revenue</p>
                <div className="font-heading text-lg font-medium text-black">
                  {formatPrice(store.analytics.revenue, { currency: store.currency })}
                </div>
                <div className="flex items-center gap-1 mt-1">
                  <DollarSign className="h-4 w-4 text-green-500" />
                  <span className="text-sm text-green-600 font-medium">
                    {store.analytics.completedOrders} completed
                  </span>
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-4 shadow-none border-none bg-blue-100/30 hover:bg-blue-100/80 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-black">Total Orders</p>
                <div className="font-heading text-lg font-medium text-black">{store.analytics.orders.toLocaleString()}</div>
                <div className="flex items-center gap-1 mt-1">
                  <ShoppingCart className="h-4 w-4 text-blue-500" />
                  <span className="text-sm text-blue-600 font-medium">
                    {store.analytics.pendingOrders} pending
                  </span>
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-4 shadow-none border-none bg-teal-100/30 hover:bg-teal-100/80 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-black">Store Views</p>
                <div className="font-heading text-lg font-medium text-black">{store.analytics.views.toLocaleString()}</div>
                <div className="flex items-center gap-1 mt-1">
                  <Eye className="h-4 w-4 text-teal-500" />
                  <span className="text-sm text-teal-600 font-medium">
                    Total views
                  </span>
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-4 shadow-none border-none bg-orange-100/30 hover:bg-orange-100/80 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-black">Avg Order Value</p>
                <div className="font-heading text-lg font-medium text-black">
                  {formatPrice(store.analytics.averageOrderValue, { currency: store.currency })}
                </div>
                <div className="flex items-center gap-1 mt-1">
                  <TrendingUp className="h-4 w-4 text-orange-500" />
                  <span className="text-sm text-orange-600 font-medium">
                    Per order
                  </span>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </Card>

      {/* Additional Stats */}
      <Card className="p-4">
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-4 w-full">
          <Card className="p-4 shadow-none border-none bg-indigo-100/30 hover:bg-indigo-100/80 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-black">Products</p>
                <div className="font-heading text-lg font-medium text-black">{store.analytics.totalProducts}</div>
                <div className="flex items-center gap-1 mt-1">
                  <Package className="h-4 w-4 text-indigo-500" />
                  <span className="text-sm text-indigo-600 font-medium">Active</span>
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-4 shadow-none border-none bg-yellow-100/30 hover:bg-yellow-100/80 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-black">Customers</p>
                <div className="font-heading text-lg font-medium text-black">{store.analytics.totalCustomers}</div>
                <div className="flex items-center gap-1 mt-1">
                  <Users className="h-4 w-4 text-yellow-500" />
                  <span className="text-sm text-yellow-600 font-medium">Unique</span>
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-4 shadow-none border-none bg-red-100/30 hover:bg-red-100/80 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-black">Cancelled Orders</p>
                <div className="font-heading text-lg font-medium text-black">{store.analytics.cancelledOrders}</div>
                <div className="flex items-center gap-1 mt-1">
                  <ShoppingCart className="h-4 w-4 text-red-500" />
                  <span className="text-sm text-red-600 font-medium">Cancelled</span>
                </div>
              </div>
            </div>
          </Card>

          {typeof store.analytics.bookings === 'number' && (
            <Card className="p-4 shadow-none border-none bg-pink-100/30 hover:bg-pink-100/80 transition-colors">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-black">Total Bookings</p>
                  <div className="font-heading text-lg font-medium text-black">{store.analytics.bookings}</div>
                  <div className="flex items-center gap-1 mt-1">
                    <Calendar className="h-4 w-4 text-pink-500" />
                    <span className="text-sm text-pink-600 font-medium">Received</span>
                  </div>
                </div>
              </div>
            </Card>
          )}
        </div>
      </Card>

      {/* Store Information and Owner */}
      <div className="grid gap-3 md:gap-4 grid-cols-1 lg:grid-cols-2">
        {/* Store Information */}
        <Card>
          <CardHeader>
            <CardTitle>Store Information</CardTitle>
            <CardDescription>Details about this store</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-start gap-3">
                <Store className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Business Name</p>
                  <p className="text-sm text-muted-foreground">{store.businessName}</p>
                </div>
              </div>

              <Separator />

              <div className="flex items-start gap-3">
                <Globe className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Store URL</p>
                  <p className="text-sm text-muted-foreground break-all">
                    sqale.shop/{store.url}
                  </p>
                </div>
              </div>

              <Separator />

              <div className="flex items-start gap-3">
                <DollarSign className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Currency</p>
                  <p className="text-sm text-muted-foreground">{store.currency}</p>
                </div>
              </div>

              {store.whatsappNumber && (
                <>
                  <Separator />
                  <div className="flex items-start gap-3">
                    <Phone className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium">WhatsApp</p>
                      <p className="text-sm text-muted-foreground">{store.whatsappNumber}</p>
                    </div>
                  </div>
                </>
              )}

              {store.location && (
                <>
                  <Separator />
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium">Location</p>
                      <p className="text-sm text-muted-foreground">{store.location}</p>
                    </div>
                  </div>
                </>
              )}

              <Separator />

              <div className="flex items-start gap-3">
                <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Created</p>
                  <p className="text-sm text-muted-foreground">{formatDate(store.createdAt)}</p>
                </div>
              </div>

              <Separator />

              <div className="flex items-start gap-3">
                <Clock className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Last Updated</p>
                  <p className="text-sm text-muted-foreground">{formatDate(store.updatedAt)}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Store Owner Information */}
        <Card>
          <CardHeader>
            <CardTitle>Store Owner</CardTitle>
            <CardDescription>Information about the store creator</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-4">
              <Avatar className="h-16 w-16">
                <AvatarImage src="" alt={store.owner.name} />
                <AvatarFallback className="text-lg">
                  {store.owner.name?.charAt(0)?.toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <h3 className="font-semibold text-lg">{store.owner.name || 'Unknown'}</h3>
                <p className="text-sm text-muted-foreground">{store.owner.email}</p>
              </div>
            </div>

            <Separator />

            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Email</p>
                  <p className="text-sm text-muted-foreground break-all">{store.owner.email}</p>
                </div>
              </div>

              {store.owner.whatsappNumber && (
                <>
                  <Separator />
                  <div className="flex items-start gap-3">
                    <Phone className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium">WhatsApp</p>
                      <p className="text-sm text-muted-foreground">{store.owner.whatsappNumber}</p>
                    </div>
                  </div>
                </>
              )}

              <Separator />

              <div className="flex items-start gap-3">
                <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Member Since</p>
                  <p className="text-sm text-muted-foreground">{formatDate(store.owner.createdAt)}</p>
                </div>
              </div>
            </div>

            <Separator />

            <div className="flex flex-col sm:flex-row gap-2">
              <Link to={`/admin/users/${store.owner._id}`} className="flex-1">
                <Button variant="outline" className="w-full">
                  <User className="h-4 w-4 mr-2" />
                  View Full User Profile
                </Button>
              </Link>
              <Button 
                variant="secondary" 
                className="flex-1"
                onClick={() => window.open(`mailto:${store.owner.email}`)}
              >
                <Mail className="h-4 w-4 mr-2" />
                Contact Owner
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this store?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete {store.storeName} and all related
              data (products, orders, bookings, discounts). The owner account
              will not be deleted. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingStore}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteStore}
              className="bg-red-600 hover:bg-red-700"
              disabled={deletingStore}
            >
              {deletingStore ? 'Deleting...' : 'Delete store'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
